import React from 'react';
import Link from 'next/link';
import styled, { css } from 'styled-components';
import styledContainerQuery from 'styled-container-query';

import CartItemClass from 'src/state/cart-item';
import useCart from 'src/hooks/use-cart';
import { formatCurrency } from 'shared/helpers/utils';
import { PlusIcon } from 'src/assets/plus';
import usePaths from 'src/hooks/use-paths';
import { Progress } from './progress';
import { MatchingItem } from './matching-item';

type ThresholdType = 'NUMBER_OF_ITEMS' | 'SPENDING_AMOUNT';

type ApplicableProduct = {
  productKey: string;
  amount: number;
};

type OfferProps = {
  shouldDisplayItems?: boolean;
  shouldLinkToSpecialOfferPage?: boolean;
  specialId?: string;
  specialName?: string;
  thresholdType: ThresholdType;
  threshold: number;
  currentAmount: number;
  cartItemIds: ApplicableProduct[];
};

const getDescription = (thresholdType: ThresholdType, threshold: number, currentAmount: number): string => {
  if (thresholdType === 'NUMBER_OF_ITEMS') {
    return `Add ${threshold - currentAmount} more eligible items to complete the offer.`;
  }
  return `Add ${formatCurrency(threshold - currentAmount, {
    trimZeroCents: true,
  })} more to complete the offer.`;
};

export const Offer = ({
  shouldDisplayItems = false,
  shouldLinkToSpecialOfferPage = false,
  specialId,
  specialName,
  thresholdType,
  threshold,
  currentAmount,
  cartItemIds,
}: OfferProps): JSX.Element => {
  const numberOfItems = thresholdType === 'NUMBER_OF_ITEMS' ? threshold : 0;
  const shouldItemsAlwaysBeSeparateRow = numberOfItems > 8;
  const shouldItemsBeSeparateRowMiddleBreakpoint = numberOfItems > 6;
  const { items } = useCart();
  const { href: specialOfferHref } = usePaths({ specials: true, id: `offer/${String(specialId)}` });
  const displayItemSlots = shouldDisplayItems && thresholdType === 'NUMBER_OF_ITEMS' && numberOfItems <= 10;
  const progress = Math.max(0, (currentAmount / threshold) * 100);

  // Create an array that includes each item based on its amount
  const matchingItems =
    displayItemSlots && cartItemIds.length
      ? (cartItemIds.flatMap((cartItemId) => {
          const item = Object.values(items).find((cartItem: CartItemClass) => cartItem.key === cartItemId.productKey);
          return item ? Array(cartItemId.amount).fill(item) : [];
        }) as CartItemClass[])
      : [];

  return (
    <Wrapper
      $shouldItemsAlwaysBeSeparateRow={shouldItemsAlwaysBeSeparateRow}
      $numberOfItems={numberOfItems}
      $shouldItemsBeSeparateRowMiddleBreakpoint={shouldItemsBeSeparateRowMiddleBreakpoint}
      data-testid={`offer-${String(specialId)}`}
    >
      <Header $shouldItemsAlwaysBeSeparateRow={shouldItemsAlwaysBeSeparateRow}>
        <Progress progress={Number.isFinite(progress) ? progress : 0} />

        <div>
          <Heading data-testid='offer-heading'>{specialName ?? 'Get the offer!'}</Heading>
          <Description data-testid='offer-description'>
            {getDescription(thresholdType, threshold, currentAmount)}
          </Description>

          {shouldLinkToSpecialOfferPage && (
            <Link href={specialOfferHref}>
              <Anchor data-testid='offer-link' href={specialOfferHref}>
                View Eligible Items
              </Anchor>
            </Link>
          )}
        </div>
      </Header>

      {displayItemSlots && (
        <Items
          data-testid='offer-items'
          $shouldItemsAlwaysBeSeparateRow={shouldItemsAlwaysBeSeparateRow}
          $numberOfItems={numberOfItems}
        >
          {matchingItems.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Item key={`${String(item.key)}-${index}`} data-testid='offer-item-matching'>
              <MatchingItem item={item} />
            </Item>
          ))}

          {Array.from({ length: threshold - matchingItems.length }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Item key={`placeholder-${index}`} data-testid='offer-item-placeholder'>
              <PlusIcon height={14} width={14} />
            </Item>
          ))}
        </Items>
      )}
    </Wrapper>
  );
};

const getNumberOfColumns = (numberOfItems: number): number => {
  if (numberOfItems <= 4) {
    return numberOfItems;
  }
  if (numberOfItems <= 6) {
    return 3;
  }
  if (numberOfItems <= 8) {
    return 4;
  }
  return 5;
};

const Header = styled.div`
  display: flex;
  gap: 12px;

  ${({ $shouldItemsAlwaysBeSeparateRow }) =>
    !$shouldItemsAlwaysBeSeparateRow &&
    css`
      min-width: 270px;
    `}
`;

const Items = styled.ul`
  display: grid;
  gap: 8px;
  list-style: none;
  justify-content: start;
  margin: 0;
  padding: 0;

  ${({ $numberOfItems }) => css`
    grid-template-columns: repeat(${$numberOfItems}, minmax(56px, 160px));
  `}

  ${({ $shouldItemsAlwaysBeSeparateRow }) =>
    !$shouldItemsAlwaysBeSeparateRow
      ? css`
          max-width: 565px;
        `
      : css`
          width: 100%;
        `}
`;

const Item = styled.li`
  align-items: center;
  background: #fff;
  border-radius: ${({ theme }) => theme.customized.radius.md};
  color: #d2d7da;
  display: flex;
  justify-content: center;
  height: 56px;
`;

const Wrapper = styledContainerQuery.div`
  background: #ceeedb;
  border-radius: ${({ theme }) => theme.customized.radius.lg};
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 16px;
  width: 100%;

  ${({ $shouldItemsAlwaysBeSeparateRow }) =>
    $shouldItemsAlwaysBeSeparateRow
      ? css`
          flex-direction: column;
        `
      : css`
          align-items: center;
        `}

  &:container(max-width: 856px) {
    ${({ $shouldItemsBeSeparateRowMiddleBreakpoint }) =>
      $shouldItemsBeSeparateRowMiddleBreakpoint &&
      css`
        align-items: flex-start;
        flex-direction: column;

        ${Header} {
          min-width: auto;
        }

        ${Items} {
          max-width: none;
          width: 100%;
        }
      `}
  }

  // Items will always be on separate row at this breakpoint
  &:container(max-width: 700px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 16px;

    ${Header} {
      min-width: auto;
    }

    ${Items} {
      width: 100%;

      ${({ $numberOfItems }) => css`
        grid-template-columns: repeat(${getNumberOfColumns($numberOfItems)}, minmax(56px, 160px));
      `}
      }}
    }
  }
`;

const Heading = styled.h2`
  font-size: 14px;
  font-weight: 600;
  line-height: ${18 / 14};
`;

const Description = styled.p`
  font-size: 14px;
  line-height: ${18 / 14};
  margin-top: 4px;
`;

const Anchor = styled.a`
  color: #00624a;
  display: block;
  font-size: 13px;
  font-weight: 600;
  line-height: ${16 / 13};
  margin-top: 4px;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;
